import {
  normalizeDisplayProvider,
  resolveDisplayModelSummary,
  resolveDisplayProvider,
} from "@arti/utilities";

import type { TaskSnapshot } from "../types";

import { getValidationErrors } from "../components/ValidationFailureSection";

/** Resolve the dominant AI provider (by token count) from usage entries or summary metadata. */
export function getTaskProvider(task: TaskSnapshot): string | null {
  if (isActiveStatus(task.status)) {
    if (task.current_display_provider) {
      return task.current_display_provider;
    }
  } else if (task.dominant_display_provider) {
    return task.dominant_display_provider;
  } else if (task.current_display_provider) {
    return task.current_display_provider;
  }

  // Usage entries carry the actual runtime provider (available on hydrated WS events)
  const entries = task.usage_summary?.entries;
  if (entries?.length) {
    const displayModel = resolveDisplayModelSummary(
      entries.map((entry) => ({
        model: entry.model,
        provider: entry.provider,
        timestampMs: entry.timestampMs,
        totalTokens: entry.tokens.totalTokens,
      })),
    );
    if (isActiveStatus(task.status)) {
      return (
        displayModel.currentDisplayProvider ??
        latestProviderFromEntries(entries)
      );
    }
    if (displayModel.dominantDisplayProvider) {
      return displayModel.dominantDisplayProvider;
    }
    return dominantProviderFromEntries(entries);
  }
  // Fallback to ai_provider in metadata (pre-computed by the summary serializer)
  const metadataProvider = resolveProviderFromMetadata(task.metadata);
  if (metadataProvider) {
    return metadataProvider;
  }
  return null;
}

function isActiveStatus(status: string): boolean {
  return status === "running" || status === "in_progress";
}

function latestProviderFromEntries(
  entries: readonly {
    model: string | null;
    provider: string;
    timestampMs: number;
  }[],
): string | null {
  let latestProvider: string | null = null;
  let latestTimestamp = -1;
  for (const entry of entries) {
    if (entry.timestampMs >= latestTimestamp) {
      latestProvider = resolveDisplayProvider(entry.model, entry.provider);
      latestTimestamp = entry.timestampMs;
    }
  }
  return latestProvider;
}

function dominantProviderFromEntries(
  entries: readonly {
    model: string | null;
    provider: string;
    tokens: { totalTokens: number };
  }[],
): string | null {
  const tokensByProvider = new Map<string, number>();
  for (const entry of entries) {
    const displayProvider = resolveDisplayProvider(entry.model, entry.provider);
    if (!displayProvider) {
      continue;
    }
    tokensByProvider.set(
      displayProvider,
      (tokensByProvider.get(displayProvider) ?? 0) + entry.tokens.totalTokens,
    );
  }
  let best: string | null = null;
  let bestTokens = -1;
  for (const [provider, tokens] of tokensByProvider) {
    if (tokens > bestTokens) {
      best = provider;
      bestTokens = tokens;
    }
  }
  return best;
}

function resolveProviderFromMetadata(
  metadata: Record<string, unknown>,
): string | null {
  const { model } = metadata;
  if (isObjectRecord(model)) {
    const fromModel = resolveDisplayProvider(
      firstString(model.modelRef, model.model),
      firstString(model.provider),
    );
    if (fromModel) {
      return fromModel;
    }
  }

  return resolveDisplayProvider(
    null,
    normalizeDisplayProvider(firstString(metadata.ai_provider)),
  );
}

function firstString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Re-export from canonical modules
export { getProviderColors } from "../components/common/providerColors";
export type { ProviderColorConfig } from "../components/common/providerColors";

// ---- Status dot/color config ----

/** Canonical status → dot color/pulse mapping used by FlowGroupHeader. */
export const STATUS_DOT_CONFIGS: {
  key: string;
  color: string;
  label: string;
  pulse: boolean;
}[] = [
  {
    color: "bg-emerald-500",
    key: "succeeded",
    label: "succeeded",
    pulse: false,
  },
  { color: "bg-emerald-500", key: "running", label: "running", pulse: true },
  { color: "bg-amber-500", key: "queued", label: "queued", pulse: false },
  { color: "bg-rose-500", key: "failed", label: "failed", pulse: false },
  { color: "bg-rose-500", key: "canceled", label: "canceled", pulse: false },
  { color: "bg-rose-500", key: "cancelled", label: "cancelled", pulse: false },
];

// Re-export from canonical module
export {
  type StatusDotConfig,
  getStatusDotConfig as getStatusDot,
} from "../components/common/statusColors";

/** Check whether a failed task has schema validation errors. */
export function isSchemaInvalid(task: TaskSnapshot): boolean {
  return task.status === "failed" && getValidationErrors(task.result) !== null;
}

// ---- Callback status helpers ----

export const isAwaitingCallback = (task: TaskSnapshot): boolean =>
  task.status === "running" &&
  task.started_at !== null &&
  task.completed_at === null;

export const getCallbackStatus = (
  task: TaskSnapshot,
): { label: string; color: string } | null => {
  if (!task.last_callback) {
    if (isAwaitingCallback(task)) {
      return {
        color:
          "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20",
        label: "Awaiting callback",
      };
    }
    return null;
  }

  const { status } = task.last_callback;
  if (status === "completed") {
    return {
      color:
        "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20",
      label: "Callback received",
    };
  }
  if (status === "failed" || status === "error") {
    return {
      color:
        "text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20",
      label: "Callback error",
    };
  }
  if (status === "progress" || status === "updated") {
    return {
      color:
        "text-slate-700 dark:text-emerald-300 bg-slate-100 dark:bg-slate-900/80 border-slate-300 dark:border-slate-700",
      label: "In progress",
    };
  }
  return {
    color:
      "text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-500/10 border-gray-200 dark:border-gray-500/20",
    label: `Callback: ${status}`,
  };
};
